import { useQuery } from '@apollo/client/react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { generateNextSeo, type NextSeoProps } from 'next-seo/pages';
import { useCallback, useState } from 'react';
import type { WebPage, WithContext } from 'schema-dts';

import { SearchInput } from '#components/Search/SearchInput';
import { RethrownError } from '#cutils/errors/RethrownError';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { useTracktor } from 'react-tracktor';

import { SchemaOrg } from '../SchemaOrg';
import { LoadingSpinner } from '../base/LoadingSpinner/LoadingSpinner';

import styles from './Search.module.scss';
import { SearchResults } from './SearchResults';
import { SearchDocument } from './__generated__/Search.generated';
import { useDebounce } from './useDebounce';

type Props = {
	schema: WithContext<WebPage>;
	seoConfig: NextSeoProps;
};

export function Search({ schema, seoConfig }: Props) {
	const router = useRouter();
	const searchTerm: string = (router.query.param as string) || '';
	const [inputValue, setInputValue] = useState(searchTerm);

	const { loading, error, data, fetchMore } = useQuery(SearchDocument, {
		notifyOnNetworkStatusChange: true,
		variables: { searchTerm: searchTerm, count: 10 },
	});

	const executeSearch = useCallback(
		(value: string) => {
			const trimmedValue = value.trim();
			if (trimmedValue !== searchTerm) {
				router.replace({ query: { param: trimmedValue } }, undefined, { shallow: true });
			}
		},
		[router, searchTerm]
	);

	const cancelDebounce = useDebounce(
		() => {
			executeSearch(inputValue);
		},
		1000,
		[inputValue]
	);

	const startSearch = useCallback(
		(value: string) => {
			cancelDebounce();
			executeSearch(value);
		},
		[cancelDebounce, executeSearch]
	);

	// focus input initially if search term is empty
	const autofocus = useCallback(
		(input: HTMLInputElement) => {
			if (searchTerm === '' && !!input) {
				input.focus();
			}
		},
		[searchTerm]
	);

	useTracktor({
		pageViewData: {
			data: { category: 'funktionsseiten', id: 'search', isNotVisitable: false, pageTitle: 'Suche', path: '/suche', rowId: 'search' },
			event: { type: 'PageViewEvent' },
		},
	});

	// this condition will be true, if the search has never been performed
	// if we are loading more results loading === true, but we already have
	// data, so we do not show the global loading spinner.
	if (loading && !data) {
		return (
			<div className={`body3 ${styles.wrapper}`}>
				<LoadingSpinner className={styles.spinner} />
			</div>
		);
	}

	if (error) {
		throw new RethrownError(error, 'SearchContainer');
	}

	if (!loading && !data?.searchResultArticles) {
		throw new Error(`No data returned from 'SearchContainer'.`);
	}

	const searchResults =
		data?.searchResultArticles?.edges
			.filter(notNullOrUndefined)
			.map((articleEdge) => articleEdge.node)
			.filter(notNullOrUndefined) ?? [];

	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>

			{/* Injects script tags with structured data. */}
			<SchemaOrg schema={schema} />

			<div className={`body3 ${styles.wrapper}`}>
				<SearchInput
					value={inputValue}
					onChange={(e) => {
						setInputValue(e.target.value);
					}}
					onKeyPress={({ key, currentTarget }) => {
						if (key === 'Enter') {
							// start search immediately
							startSearch(currentTarget.value);
						}
					}}
					onClear={() => {
						setInputValue('');
						// start search immediately
						startSearch('');
					}}
					ref={autofocus}
				/>

				<SearchResults
					hasNextPage={data?.searchResultArticles?.pageInfo.hasNextPage ?? false}
					onLoadMore={() => {
						fetchMore({
							variables: { cursor: data?.searchResultArticles?.pageInfo.cursor },
						});
					}}
					isLoading={loading}
					searchResultArticles={searchResults}
					totalCount={data?.searchResultArticles?.totalCount ?? null}
				/>
			</div>
		</>
	);
}
